import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Upload, Send, Trash2, Plus, Image as ImageIcon, CheckCircle2, Clock, XCircle,
  Camera, ExternalLink,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { useSeo } from "@/lib/useSeo";
import {
  hotelierMe, hotelierLogout, hotelierGetHotel, hotelierUpdateHotel,
  hotelierUploadMain, hotelierGalleryAdd, hotelierGalleryRemove, hotelierSubmit,
  hotelierListUpdates, hotelierAddUpdate, hotelierDeleteUpdate,
  hotelierUploadUpdateImage, fileUrl,
} from "@/lib/api";

const STATUS_LABEL = {
  draft: { label: "Bozza", icon: Clock, color: "text-neutral-500" },
  pending: { label: "In revisione", icon: Clock, color: "text-amber-600" },
  approved: { label: "Pubblicato", icon: CheckCircle2, color: "text-emerald-700" },
  rejected: { label: "Rifiutato", icon: XCircle, color: "text-red-600" },
};

export default function HotelierDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [hotel, setHotel] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [busy, setBusy] = useState(false);

  useSeo({ title: "Dashboard Hotelier — SuiteCrowd", description: "Gestisci il tuo hotel su SuiteCrowd" });

  useEffect(() => {
    (async () => {
      try {
        const m = await hotelierMe();
        setMe(m);
        const [h, u] = await Promise.all([hotelierGetHotel(), hotelierListUpdates()]);
        setHotel(h);
        setUpdates(u);
      } catch {
        navigate("/hotelier-login", { replace: true });
      }
    })();
  }, [navigate]);

  const logout = async () => {
    await hotelierLogout();
    navigate("/hotelier-login", { replace: true });
  };

  const saveText = async (patch) => {
    setBusy(true);
    try {
      const updated = await hotelierUpdateHotel(patch);
      setHotel(updated);
      toast.success("Salvato");
    } catch {
      toast.error("Errore salvataggio");
    } finally {
      setBusy(false);
    }
  };

  const submitForReview = async () => {
    try {
      await hotelierSubmit();
      const h = await hotelierGetHotel();
      setHotel(h);
      toast.success("Progetto inviato in revisione.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio");
    }
  };

  if (!me || !hotel) {
    return (
      <div className="min-h-screen flex items-center justify-center text-neutral-500 text-sm tracking-wider">
        Caricamento...
      </div>
    );
  }

  const StatusInfo = STATUS_LABEL[hotel.status] || STATUS_LABEL.draft;

  return (
    <div className="min-h-screen bg-[#F7F7F8]">
      <header className="bg-white border-b border-[#E5E5E5] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-6 flex justify-between items-center">
          <div className="flex items-center gap-8">
            <Logo variant="dark" size="md" />
            <div className="sc-overline">Portale Hotelier</div>
          </div>
          <div className="flex items-center gap-5">
            <div className={`inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase ${StatusInfo.color}`}>
              <StatusInfo.icon size={14} strokeWidth={1.5} /> {StatusInfo.label}
            </div>
            <div className="text-xs text-neutral-500 hidden md:block">{me.email}</div>
            {hotel.status === "approved" && (
              <Link
                to={`/hotel/${hotel.hotel_id}`}
                className="sc-btn-ghost text-[11px] inline-flex items-center gap-2"
                data-testid="preview-public-link"
              >
                <ExternalLink size={12} /> Anteprima pubblica
              </Link>
            )}
            <button onClick={logout} className="sc-btn-ghost inline-flex items-center gap-2" data-testid="hotelier-logout">
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-16 space-y-16">
        {/* Status banner */}
        {hotel.status === "draft" && (
          <div className="border border-[#E5E5E5] bg-white p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="sc-overline mb-2">Non ancora pubblicato</div>
              <div className="text-sm text-neutral-600">
                Compila descrizione, immagine principale e target di raccolta,
                poi invia in revisione. Nulla è visibile al pubblico fino
                all'approvazione.
              </div>
            </div>
            <button
              onClick={submitForReview}
              className="sc-btn-primary inline-flex items-center gap-2"
              data-testid="submit-review-btn"
            >
              <Send size={14} /> Invia in revisione
            </button>
          </div>
        )}
        {hotel.status === "pending" && (
          <div className="border border-amber-200 bg-amber-50 p-6 md:p-8">
            <div className="sc-overline mb-2" style={{ color: "#78350F" }}>In revisione</div>
            <div className="text-sm text-amber-900">
              Il tuo progetto è in valutazione dal team SuiteCrowd. Riceverai un'email
              non appena sarà pubblicato.
            </div>
          </div>
        )}

        {/* Main image */}
        <section>
          <div className="sc-overline mb-4">Immagine principale</div>
          <div className="bg-white border border-[#E5E5E5]">
            {hotel.image_url ? (
              <img src={fileUrl(hotel.image_url)} alt="main" className="w-full h-72 object-cover" />
            ) : (
              <div className="w-full h-72 flex items-center justify-center text-neutral-400">
                <ImageIcon size={40} strokeWidth={1} />
              </div>
            )}
            <div className="p-6 flex flex-col md:flex-row md:items-center gap-4 justify-between">
              <p className="text-sm text-neutral-500 max-w-lg">
                Foto in evidenza che apparirà nell'anteprima del progetto. JPEG/PNG/WebP, max 6MB.
              </p>
              <label className="sc-btn-primary inline-flex items-center gap-2 cursor-pointer" data-testid="upload-main-btn">
                <Upload size={14} /> Carica foto principale
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    try {
                      const { image_url } = await hotelierUploadMain(f);
                      setHotel({ ...hotel, image_url });
                      toast.success("Foto principale aggiornata");
                    } catch {
                      toast.error("Upload non riuscito");
                    }
                  }}
                />
              </label>
            </div>
          </div>
        </section>

        {/* Text fields */}
        <TextEditor hotel={hotel} onSave={saveText} busy={busy} />

        {/* Gallery */}
        <section>
          <div className="sc-overline mb-4">Galleria hotel</div>
          <div className="bg-white border border-[#E5E5E5] p-6 md:p-8 space-y-6">
            <p className="text-sm text-neutral-500 max-w-lg">
              Foto delle camere, aree comuni, esterni. Vengono mostrate nella scheda pubblica.
            </p>
            {hotel.gallery?.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {hotel.gallery.map((path) => (
                  <div key={path} className="relative group aspect-square">
                    <img src={fileUrl(`/api/files/${path}`)} alt="gallery" className="w-full h-full object-cover" />
                    <button
                      onClick={async () => {
                        await hotelierGalleryRemove(path);
                        setHotel({ ...hotel, gallery: hotel.gallery.filter((p) => p !== path) });
                      }}
                      className="absolute top-2 right-2 bg-white/90 p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Elimina"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label className="sc-btn-ghost inline-flex items-center gap-2 cursor-pointer" data-testid="gallery-add-btn">
              <Plus size={14} /> Aggiungi foto in galleria
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  try {
                    const up = await hotelierGalleryAdd(f);
                    setHotel({ ...hotel, gallery: [...(hotel.gallery || []), up.storage_path] });
                    toast.success("Foto aggiunta");
                  } catch {
                    toast.error("Upload non riuscito");
                  }
                }}
              />
            </label>
          </div>
        </section>

        {/* Timeline updates */}
        <TimelineEditor
          updates={updates}
          onAdd={(u) => setUpdates([u, ...updates])}
          onDelete={(id) => setUpdates(updates.filter((u) => u.update_id !== id))}
        />
      </main>
    </div>
  );
}

function TextEditor({ hotel, onSave, busy }) {
  const [form, setForm] = useState({
    name: hotel.name,
    location: hotel.location,
    description: hotel.description || "",
    goal_amount: hotel.goal_amount,
    days_left: hotel.days_left,
    quota: hotel.quota,
    stay_days: hotel.stay_days,
  });
  useEffect(() => {
    setForm({
      name: hotel.name,
      location: hotel.location,
      description: hotel.description || "",
      goal_amount: hotel.goal_amount,
      days_left: hotel.days_left,
      quota: hotel.quota,
      stay_days: hotel.stay_days,
    });
  }, [hotel.hotel_id]);

  const set = (k) => (e) => {
    const v = e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setForm({ ...form, [k]: v });
  };
  return (
    <section>
      <div className="flex justify-between items-center mb-4">
        <div className="sc-overline">Contenuti pubblici</div>
        <button
          onClick={() => onSave(form)}
          disabled={busy}
          className="sc-btn-primary text-[11px]"
          data-testid="save-text-btn"
        >
          {busy ? "Salvo..." : "Salva modifiche"}
        </button>
      </div>
      <div className="bg-white border border-[#E5E5E5] p-6 md:p-8 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="sc-overline block mb-2">Nome hotel</label>
            <input value={form.name} onChange={set("name")} className="sc-input-light" data-testid="edit-name" />
          </div>
          <div>
            <label className="sc-overline block mb-2">Località</label>
            <input value={form.location} onChange={set("location")} className="sc-input-light" data-testid="edit-location" />
          </div>
        </div>
        <div>
          <label className="sc-overline block mb-2">Descrizione del progetto</label>
          <textarea
            rows={6}
            value={form.description}
            onChange={set("description")}
            className="sc-input-light"
            data-testid="edit-description"
            placeholder="Racconta il progetto di ristrutturazione, la storia della struttura, gli interventi previsti..."
          />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="sc-overline block mb-2">Target €</label>
            <input type="number" value={form.goal_amount} onChange={set("goal_amount")} className="sc-input-light" data-testid="edit-goal" />
          </div>
          <div>
            <label className="sc-overline block mb-2">Giorni rimasti</label>
            <input type="number" value={form.days_left} onChange={set("days_left")} className="sc-input-light" data-testid="edit-days" />
          </div>
          <div>
            <label className="sc-overline block mb-2">Quota €</label>
            <input type="number" value={form.quota} onChange={set("quota")} className="sc-input-light" data-testid="edit-quota" />
          </div>
          <div>
            <label className="sc-overline block mb-2">Giorni voucher</label>
            <input type="number" value={form.stay_days} onChange={set("stay_days")} className="sc-input-light" data-testid="edit-stay" />
          </div>
        </div>
      </div>
    </section>
  );
}

function TimelineEditor({ updates, onAdd, onDelete }) {
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const post = async () => {
    if (!caption || !file) {
      toast.error("Aggiungi foto e didascalia");
      return;
    }
    setBusy(true);
    try {
      const up = await hotelierUploadUpdateImage(file);
      const created = await hotelierAddUpdate(caption, up.storage_path);
      onAdd(created);
      setCaption("");
      setFile(null);
      toast.success("Aggiornamento pubblicato");
    } catch {
      toast.error("Errore pubblicazione");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <div className="sc-overline mb-4">Avanzamento lavori</div>
      <div className="bg-white border border-[#E5E5E5] p-6 md:p-8 space-y-5">
        <p className="text-sm text-neutral-500">
          Pubblica foto del cantiere e note di avanzamento. Sono visibili ai co-finanziatori nella scheda pubblica.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="sc-overline block mb-2">Didascalia</label>
            <input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="sc-input-light"
              placeholder="es. Completato il rifacimento della facciata liberty"
              data-testid="update-caption"
            />
          </div>
          <div className="flex gap-3">
            <label className="sc-btn-ghost inline-flex items-center gap-2 cursor-pointer flex-1 justify-center" data-testid="update-file-btn">
              <Camera size={14} /> {file ? file.name.slice(0, 12) + "..." : "Scegli foto"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
            <button
              onClick={post}
              disabled={busy}
              className="sc-btn-primary text-[11px]"
              data-testid="update-post-btn"
            >
              {busy ? "..." : "Pubblica"}
            </button>
          </div>
        </div>

        {updates.length > 0 && (
          <div className="pt-6 border-t border-[#E5E5E5] space-y-4">
            {updates.map((u) => (
              <div key={u.update_id} className="flex gap-4 items-start">
                <img src={fileUrl(u.image_url)} alt="update" className="w-24 h-24 object-cover flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-sm text-[#121212]">{u.caption}</div>
                  <div className="text-[11px] text-neutral-400 tracking-wider mt-1 uppercase">
                    {new Date(u.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}
                  </div>
                </div>
                <button
                  onClick={async () => {
                    await hotelierDeleteUpdate(u.update_id);
                    onDelete(u.update_id);
                  }}
                  className="text-neutral-400 hover:text-neutral-700 transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
